import { useMemo } from 'react'

import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'

import { type QueryClient, useQueryClient } from '@tanstack/react-query'

import { convert } from '@/lib/convert'

import { paths } from '@/config/paths'
import { PERMISSIONS } from '@/config/permissions'

import { DashboardLayout } from '@/layouts/dashboard/DashboardLayout'
import PublicLayout from '@/layouts/public/PublicLayout'

import ProfileSettingsLayout from '@/features/preferences/routes/ProfileSettingsLayout'
import AppSettingsLayout from '@/features/settings/routes/AppSettingsLayout'

import { LoginPage } from '@/pages/Login'
import MaintenancePage from '@/pages/MaintenancePage'
import { RegisterPage } from '@/pages/Register'
import RouteErrorPage from '@/pages/errors/RouteErrorPage'
import GuestRoute from '@/routes/GuestRoute'
import MembershipProtectedRoute from '@/routes/MembershipProtectedRoute'
import PermissionProtectedRoute from '@/routes/PermissionProtectedRoute'
import PrivateRoute from '@/routes/PrivateRoute'
import RoleProtectedRoute from '@/routes/RoleProtectedRoute'
import ViewOnlyMembershipRoute from '@/routes/ViewOnlyMembershipRoute'

// Used only to satisfy React Router's HydrateFallback requirement; renders nothing visually
const RouteHydrateFallback = () => null

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      HydrateFallback: RouteHydrateFallback,
      element: <PublicLayout />,
      errorElement: <RouteErrorPage />,
      children: [
        {
          path: paths.public.paymentResult.path,
          lazy: () => import('@/pages/PaymentResultPage').then(convert(queryClient)),
        },
        {
          element: <GuestRoute />,
          children: [
            {
              path: paths.public.home.path,
              element: <Navigate to={paths.public.auth.login.path} replace />,
            },
            {
              path: paths.public.auth.login.path,
              element: <LoginPage />,
            },
            {
              path: paths.public.auth.register.path,
              element: <RegisterPage />,
            },
            {
              path: paths.public.auth.resetPassword.path,
              lazy: () => import('@/pages/Reset.Password').then(convert(queryClient)),
            },
            {
              path: paths.public.auth.verifyEmail.path,
              lazy: () => import('@/pages/VerifyEmailPage').then(convert(queryClient)),
            },
            {
              path: paths.public.auth.verificationStatus.path,
              lazy: () => import('@/pages/AccountVerificationStatusPage').then(convert(queryClient)),
            },
            {
              path: paths.public.auth.setPassword.path,
              lazy: () => import('@/pages/SetPassword').then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.public.legal.privacyPolicy.path,
          lazy: () => import('@/pages/PrivacyPolicyPage').then(convert(queryClient)),
        },
        {
          path: paths.public.legal.terms.path,
          lazy: () => import('@/pages/TermsPage').then(convert(queryClient)),
        },
      ],
    },
    {
      HydrateFallback: RouteHydrateFallback,
      element: <PrivateRoute />,
      errorElement: <RouteErrorPage />,
      children: [
        {
          path: '/my-account/email/confirm',
          lazy: () => import('@/pages/ConfirmEmailChangePage').then(convert(queryClient)),
        },
        {
          element: <DashboardLayout />,
          children: [
            {
              index: true,
              lazy: () => import('@/routes/RoleDashboards').then(convert(queryClient)),
            },
            // Super Admin Only - System Administration
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin']} />,
              children: [
                {
                  element: (
                    <PermissionProtectedRoute
                      requiredPermissions={[PERMISSIONS.SETTINGS.VIEW, PERMISSIONS.SETTINGS.UPDATE]}
                    />
                  ),
                  children: [
                    {
                      path: paths.dashboard.roles.permissions.path,
                      lazy: () =>
                        import('@/features/access-control/routes/RolePermissionEditor').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.settings.root.path,
                      element: <AppSettingsLayout />,
                      children: [
                        {
                          index: true,
                          lazy: () => import('@/features/settings/routes/GeneralSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.branding.path.split('/').pop(),
                          lazy: () => import('@/features/settings/routes/BrandingSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.mailBranding.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/MailBrandingSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.security.path.split('/').pop(),
                          lazy: () => import('@/features/settings/routes/SecuritySettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.payments.path.split('/').pop(),
                          lazy: () => import('@/features/settings/routes/PaymentSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.localization.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/LocalizationSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.integrations.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/IntegrationSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.notifications.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/NotificationSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.mail.path.split('/').pop(),
                          lazy: () => import('@/features/settings/routes/MailSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.bookingReminders.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/BookingRemindersSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.membership.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/MembershipSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.bookings.path.split('/').pop(),
                          lazy: () => import('@/features/settings/routes/BookingSettings').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.settings.gymOperations.path.split('/').pop(),
                          lazy: () =>
                            import('@/features/settings/routes/GymOperationsSettings').then(convert(queryClient)),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: paths.dashboard.auditLogs.root.path,
                  lazy: () => import('@/features/audit-logs/routes/AuditLogsPage').then(convert(queryClient)),
                },
                {
                  path: paths.dashboard.apiHealth.root.path,
                  lazy: () => import('@/features/api-health/routes/ApiHealthPage').then(convert(queryClient)),
                },
              ],
            },
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin', 'gym_manager', 'assistant']} />,
              children: [
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.BRANCHES.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.branches.root.path,
                      lazy: () => import('@/features/branches/routes/AdminBranchesPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.BRANCHES.CREATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.branches.root.path}/create`,
                      lazy: () =>
                        import('@/features/branches/routes/AdminCreateBranchesPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.BRANCHES.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.branches.root.path}/:id/edit`,
                      lazy: () => import('@/features/branches/routes/AdminEditBranchesPage').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin']} />,
              children: [
                {
                  path: `${paths.dashboard.branches.root.path}/:id/settings`,
                  lazy: () => import('@/features/branches/routes/AdminBranchSettingsPage').then(convert(queryClient)),
                },
              ],
            },
            // Full Management Access
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin', 'gym_manager', 'assistant']} />,
              children: [
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MEMBERS.BAN]} />,
                  children: [
                    {
                      path: paths.dashboard.bannedUsers.root.path,
                      lazy: () => import('@/features/security/routes/AdminBannedUsers').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MEMBERS.DELETE]} />,
                  children: [
                    {
                      path: paths.dashboard.deletionRequests.root.path,
                      lazy: () => import('@/features/security/routes/AdminDeletionRequests').then(convert(queryClient)),
                    },
                  ],
                },
                // Staff Management (Super Admin & Gym Manager only)
                {
                  path: 'staff',
                  children: [
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.ASSISTANTS.VIEW, PERMISSIONS.STAFF.VIEW]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.assistants.root.path,
                          lazy: () =>
                            import('@/features/users/assistants/routes/AssistantsPage').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.assistants.view.path,
                          lazy: () =>
                            import('@/features/users/assistants/routes/AdminViewAssistantsPage').then(
                              convert(queryClient)
                            ),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.ASSISTANTS.UPDATE, PERMISSIONS.STAFF.UPDATE]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.assistants.edit.path,
                          lazy: () =>
                            import('@/features/users/assistants/routes/AdminEditAssistantsPage').then(
                              convert(queryClient)
                            ),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.ASSISTANTS.CREATE, PERMISSIONS.STAFF.CREATE]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.assistants.create.path,
                          lazy: () =>
                            import('@/features/users/assistants/routes/AdminCreateAssistantsPage').then(
                              convert(queryClient)
                            ),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.MANAGERS.VIEW, PERMISSIONS.STAFF.VIEW]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.gymManagers.root.path,
                          lazy: () =>
                            import('@/features/users/gym-managers/routes/GymManagersPage').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.gymManagers.view.path,
                          lazy: () =>
                            import('@/features/users/gym-managers/routes/AdminViewGymManagersPage').then(
                              convert(queryClient)
                            ),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.MANAGERS.UPDATE, PERMISSIONS.STAFF.UPDATE]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.gymManagers.edit.path,
                          lazy: () =>
                            import('@/features/users/gym-managers/routes/AdminEditGymManagersPage').then(
                              convert(queryClient)
                            ),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.MANAGERS.CREATE, PERMISSIONS.STAFF.CREATE]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.gymManagers.create.path,
                          lazy: () =>
                            import('@/features/users/gym-managers/routes/AdminCreateGymManagersPage').then(
                              convert(queryClient)
                            ),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.COACHES.VIEW, PERMISSIONS.STAFF.VIEW]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.coaches.root.path,
                          lazy: () => import('@/features/users/coaches/routes/CoachesPage').then(convert(queryClient)),
                        },
                        {
                          path: paths.dashboard.coaches.view.path,
                          lazy: () =>
                            import('@/features/users/coaches/routes/AdminViewCoachesPage').then(convert(queryClient)),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.COACHES.UPDATE, PERMISSIONS.STAFF.UPDATE]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.coaches.edit.path,
                          lazy: () =>
                            import('@/features/users/coaches/routes/AdminEditCoachesPage').then(convert(queryClient)),
                        },
                      ],
                    },
                    {
                      element: (
                        <PermissionProtectedRoute
                          requiredPermissions={[PERMISSIONS.COACHES.CREATE, PERMISSIONS.STAFF.CREATE]}
                        />
                      ),
                      children: [
                        {
                          path: paths.dashboard.coaches.create.path,
                          lazy: () =>
                            import('@/features/users/coaches/routes/AdminCreateCoachesPage').then(convert(queryClient)),
                        },
                      ],
                    },
                  ],
                },
                // Plans & Payments
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.PLANS.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.plans.root.path,
                      lazy: () => import('@/features/plans/routes/AdminPlansPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.PLANS.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.plans.root.path}/:id/edit`,
                      lazy: () => import('@/features/plans/routes/AdminEditPlanPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.PLANS.CREATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.plans.root.path}/create`,
                      lazy: () => import('@/features/plans/routes/AdminCreatePlanPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.invoices.root.path,
                      lazy: () => import('@/features/payments/routes/AdminPaymentsPage').then(convert(queryClient)),
                    },
                    {
                      path: `${paths.dashboard.invoices.root.path}/:id/view`,
                      lazy: () =>
                        import('@/features/payments/routes/AdminPaymentDetailsPage').then(convert(queryClient)),
                    },
                  ],
                },
                // Reports
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS.ATTENDANCE]} />,
                  children: [
                    {
                      path: paths.dashboard.reports.attendance.path,
                      lazy: () =>
                        import('@/features/reports/routes/AdminAttendanceReportsPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS.MEMBERSHIP]} />,
                  children: [
                    {
                      path: paths.dashboard.reports.membership.path,
                      lazy: () =>
                        import('@/features/reports/routes/AdminMembershipReportsPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS.BOOKINGS]} />,
                  children: [
                    {
                      path: paths.dashboard.reports.bookings.path,
                      lazy: () =>
                        import('@/features/reports/routes/AdminBookingsReportsPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS.FINANCES]} />,
                  children: [
                    {
                      path: paths.dashboard.reports.financial.path,
                      lazy: () =>
                        import('@/features/reports/routes/AdminFinancialReportsPage').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            // Super Admin + Gym Manager + Assistant - Shared Access
            // Use AccessProtected inside components to control create/edit/delete actions
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin', 'gym_manager', 'assistant']} />,
              children: [
                // Member Management (All can view; create/delete protected via AccessProtected in components)
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MEMBERS.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.members.root.path,
                      lazy: () => import('@/features/users/members/routes/MembersPage').then(convert(queryClient)),
                    },
                    {
                      path: `${paths.dashboard.members.root.path}/:id/view`,
                      lazy: () =>
                        import('@/features/users/members/routes/AdminViewMemberPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MEMBERS.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.members.root.path}/:id/edit`,
                      lazy: () =>
                        import('@/features/users/members/routes/AdminEditMemberPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MEMBERS.CREATE]} />,
                  children: [
                    {
                      path: paths.dashboard.members.create.path,
                      lazy: () =>
                        import('@/features/users/members/routes/AdminCreateMemberPage').then(convert(queryClient)),
                    },
                  ],
                },
                // Health Data (All can view/create/update; delete protected via AccessProtected)
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.HEALTH.CREATE]} />,
                  children: [
                    {
                      path: paths.dashboard.members.health.goals.create.path,
                      lazy: () =>
                        import('@/features/health/routes/admin/AdminCreateGoalPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.members.health.measurements.create.path,
                      lazy: () =>
                        import('@/features/health/routes/admin/AdminCreateMeasurementPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.members.health.vitals.create.path,
                      lazy: () =>
                        import('@/features/health/routes/admin/AdminCreateVitalPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.HEALTH.UPDATE]} />,
                  children: [
                    {
                      path: paths.dashboard.members.health.goals.edit.path,
                      lazy: () => import('@/features/health/routes/admin/AdminEditGoalPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.members.health.measurements.edit.path,
                      lazy: () =>
                        import('@/features/health/routes/admin/AdminEditMeasurementPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.members.health.vitals.edit.path,
                      lazy: () =>
                        import('@/features/health/routes/admin/AdminEditVitalPage').then(convert(queryClient)),
                    },
                  ],
                },
                // Classes (All can view; create/edit protected via AccessProtected in components)
                {
                  element: (
                    <PermissionProtectedRoute
                      requiredPermissions={[PERMISSIONS.CLASSES.VIEW, PERMISSIONS.CLASSES.EXPORT]}
                    />
                  ),
                  children: [
                    {
                      path: paths.dashboard.classes.root.path,
                      lazy: () =>
                        import('@/features/gym-class/classes/routes/AdminClassesPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.VIEW]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classes.root.path}/:id/view`,
                      lazy: () =>
                        import('@/features/gym-class/classes/routes/AdminViewClassPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classes.root.path}/:id/edit`,
                      lazy: () =>
                        import('@/features/gym-class/classes/routes/AdminEditClassPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.CREATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classes.root.path}/create`,
                      lazy: () =>
                        import('@/features/gym-class/classes/routes/AdminCreateClassPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.classTypes.root.path,
                      lazy: () =>
                        import('@/features/gym-class/class-types/routes/AdminClassTypesPage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.VIEW]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classTypes.root.path}/:id/view`,
                      lazy: () =>
                        import('@/features/gym-class/class-types/routes/AdminClassTypeViewPage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classTypes.root.path}/:id/edit`,
                      lazy: () =>
                        import('@/features/gym-class/class-types/routes/AdminClassTypeEditPage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.CREATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classTypes.root.path}/create`,
                      lazy: () =>
                        import('@/features/gym-class/class-types/routes/AdminClassTypeCreatePage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.classTemplates.root.path,
                      lazy: () =>
                        import('@/features/gym-class/class-templates/routes/AdminClassTemplatesPage').then(
                          convert(queryClient)
                        ),
                    },
                    {
                      path: `${paths.dashboard.classTemplates.root.path}/:id/view`,
                      lazy: () =>
                        import('@/features/gym-class/class-templates/routes/AdminViewClassTemplatesPage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },

                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.CREATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classTemplates.root.path}/create`,
                      lazy: () =>
                        import('@/features/gym-class/class-templates/routes/AdminCreateClassTemplatesPage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CLASSES.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.classTemplates.root.path}/:id/edit`,
                      lazy: () =>
                        import('@/features/gym-class/class-templates/routes/AdminEditClassTemplatesPage').then(
                          convert(queryClient)
                        ),
                    },
                    {
                      path: `${paths.dashboard.classTemplates.root.path}/:id/generate`,
                      lazy: () =>
                        import('@/features/gym-class/class-templates/routes/AdminGenerateFromTemplatePage').then(
                          convert(queryClient)
                        ),
                    },
                  ],
                },
                // Bookings (All can view/cancel; create/edit protected via AccessProtected in components)
                {
                  element: (
                    <PermissionProtectedRoute
                      requiredPermissions={[PERMISSIONS.BOOKINGS.VIEW, PERMISSIONS.BOOKINGS.CANCEL]}
                    />
                  ),
                  children: [
                    {
                      path: paths.dashboard.bookings.root.path,
                      lazy: () => import('@/features/bookings/routes/AdminBookingsPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: (
                    <PermissionProtectedRoute
                      requiredPermissions={[PERMISSIONS.BOOKINGS.UPDATE, PERMISSIONS.BOOKINGS.OVERRIDE]}
                    />
                  ),
                  children: [
                    {
                      path: `${paths.dashboard.bookings.root.path}/:id/edit`,
                      lazy: () => import('@/features/bookings/routes/AdminEditBookingsPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.BOOKINGS.CREATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.bookings.root.path}/create`,
                      lazy: () =>
                        import('@/features/bookings/routes/AdminCreateBookingsPage').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            // Member routes that don't require active membership
            {
              element: <RoleProtectedRoute requiredRoles={['member']} />,
              children: [
                {
                  path: paths.dashboard.member.selectPlan.path,
                  lazy: () =>
                    import('@/features/users/members/routes/MemberPlanAssignmentPage').then(convert(queryClient)),
                },
                {
                  path: paths.dashboard.member.checkout.path,
                  lazy: () => import('@/features/users/members/routes/MemberCheckoutPage').then(convert(queryClient)),
                },
                {
                  path: `${paths.dashboard.member.checkout.path}/:paymentId`,
                  lazy: () => import('@/features/users/members/routes/MemberCheckoutPage').then(convert(queryClient)),
                },
                {
                  path: paths.dashboard.member.myMembership.path,
                  lazy: () => import('@/features/membership/routes/MyMembership').then(convert(queryClient)),
                },
              ],
            },
            // Member routes with view-only access (active or paused membership)
            {
              element: <RoleProtectedRoute requiredRoles={['member']} />,
              children: [
                {
                  element: <ViewOnlyMembershipRoute />,
                  children: [
                    {
                      path: paths.dashboard.member.myPayments.path,
                      lazy: () => import('@/features/payments/routes/MyPaymentsPage').then(convert(queryClient)),
                    },
                    {
                      path: `${paths.dashboard.member.myPayments.path}/:id/view`,
                      lazy: () => import('@/features/payments/routes/MyPaymentViewPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.member.myNotes.path,
                      lazy: () => import('@/features/notes/routes/MyNotesPage').then(convert(queryClient)),
                    },
                    // Health Dashboard
                    {
                      path: paths.dashboard.member.myHealthTracking.path,
                      lazy: () => import('@/features/health/routes/MyHealthDashboard').then(convert(queryClient)),
                    },
                    // Health Tracking - Vitals
                    {
                      path: paths.dashboard.healthTracking.vitals.root.path,
                      lazy: () => import('@/features/health/routes/member/VitalsPage').then(convert(queryClient)),
                    },
                    // Health Tracking - Measurements
                    {
                      path: paths.dashboard.healthTracking.measurements.root.path,
                      lazy: () => import('@/features/health/routes/member/MeasurementsPage').then(convert(queryClient)),
                    },
                    // Health Tracking - Goals
                    {
                      path: paths.dashboard.healthTracking.goals.root.path,
                      lazy: () => import('@/features/health/routes/member/GoalsPage').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            // Member routes requiring active membership (booking access)
            {
              element: <RoleProtectedRoute requiredRoles={['member']} />,
              children: [
                {
                  element: <MembershipProtectedRoute />,
                  children: [
                    {
                      path: paths.dashboard.member.myBookings.path,
                      lazy: () => import('@/features/bookings/routes/MyBookingsPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.member.bookClass.path,
                      lazy: () => import('@/features/bookings/routes/BookClassPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.healthTracking.vitals.create.path,
                      lazy: () => import('@/features/health/routes/member/CreateVitalPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.healthTracking.vitals.edit.path,
                      lazy: () => import('@/features/health/routes/member/EditVitalPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.healthTracking.measurements.create.path,
                      lazy: () =>
                        import('@/features/health/routes/member/CreateMeasurementPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.healthTracking.measurements.edit.path,
                      lazy: () =>
                        import('@/features/health/routes/member/EditMeasurementPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.healthTracking.goals.create.path,
                      lazy: () => import('@/features/health/routes/member/CreateGoalPage').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.healthTracking.goals.edit.path,
                      lazy: () => import('@/features/health/routes/member/EditGoalPage').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin', 'gym_manager', 'assistant', 'member']} />,
              children: [
                {
                  path: paths.dashboard.profile.root.path,
                  element: <ProfileSettingsLayout />,
                  children: [
                    {
                      index: true,
                      lazy: () => import('@/features/preferences/routes/ProfileSettings').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.profile.security.path.split('/').pop(), // "security"
                      lazy: () =>
                        import('@/features/preferences/routes/ProfileSecuritySettings').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.profile.localization.path.split('/').pop(), // "localization"
                      lazy: () =>
                        import('@/features/preferences/routes/ProfileLocalizationSettings').then(convert(queryClient)),
                    },
                    {
                      path: paths.dashboard.profile.notifications.path.split('/').pop(), // "notifications"
                      lazy: () =>
                        import('@/features/preferences/routes/ProfileNotificationSettings').then(convert(queryClient)),
                    },
                  ],
                },
                // Support (available to all authenticated users)
                {
                  path: paths.dashboard.support.root.path,
                  lazy: () => import('@/features/support/routes/SupportPage').then(convert(queryClient)),
                },
                // Help page (for super_admin)
                // {
                //   element: <RoleProtectedRoute requiredRoles={['super_admin']} />,
                //   children: [
                //     {
                //       path: paths.dashboard.support.help.path,
                //       lazy: () => import('@/features/support/routes/AdminHelpPage').then(convert(queryClient)),
                //     },
                //   ],
                // },
              ],
            },
            // Check-in routes - Staff only with proper permissions
            {
              element: <RoleProtectedRoute requiredRoles={['super_admin', 'gym_manager', 'assistant']} />,
              children: [
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CHECKINS.CREATE]} />,
                  children: [
                    {
                      path: paths.dashboard.checkIn.root.path,
                      lazy: () => import('@/features/checkin/routes/AdminCheckInPage').then(convert(queryClient)),
                    },
                    {
                      path: `${paths.dashboard.checkIns.root.path}/create`,
                      lazy: () => import('@/features/checkin/routes/AdminCreateCheckInPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CHECKINS.UPDATE]} />,
                  children: [
                    {
                      path: `${paths.dashboard.checkIns.root.path}/:id/edit`,
                      lazy: () => import('@/features/checkin/routes/AdminEditCheckInPage').then(convert(queryClient)),
                    },
                  ],
                },
                {
                  element: <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CHECKINS.VIEW]} />,
                  children: [
                    {
                      path: paths.dashboard.checkIns.root.path,
                      lazy: () => import('@/features/checkin/routes/AdminCheckInsPage').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: paths.public.unauthorized.path,
      errorElement: <RouteErrorPage />,
      lazy: () => import('@/pages/errors/UnauthorizedPage').then(convert(queryClient)),
    },
    {
      path: paths.public.serverError.path,
      errorElement: <RouteErrorPage />,
      lazy: () => import('@/pages/errors/ServerErrorPage').then(convert(queryClient)),
    },
    {
      path: '/maintenance',
      errorElement: <RouteErrorPage />,
      element: <MaintenancePage />,
    },
    {
      path: paths.notFound.path,
      errorElement: <RouteErrorPage />,
      lazy: () => import('@/pages/errors/NotFoundPage').then(convert(queryClient)),
    },
  ])

export const AppRouter = () => {
  const queryClient = useQueryClient()

  const router = useMemo(() => createAppRouter(queryClient), [queryClient])

  return <RouterProvider router={router} />
}
